/**
 * Set Reminder Dialog Component
 * Allows users to set reminders and next expected update dates
 */

import React, { useState } from 'react';
import { Task, Epic, Project, Sprint, Reminder } from '../types';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Bell, Calendar, Clock, Repeat, X, AlertCircle } from 'lucide-react';
import { generateId } from '../utils/idGenerator';

interface SetReminderDialogProps {
  entity: Task | Epic | Project | Sprint;
  entityType: 'task' | 'epic' | 'project' | 'sprint';
  currentUserId: string;
  assigneeIds?: string[];
  onSave: (reminder: Reminder, nextUpdateDate?: string) => void;
  onClose: () => void;
}

export default function SetReminderDialog({
  entity,
  entityType,
  currentUserId,
  assigneeIds = [],
  onSave,
  onClose,
}: SetReminderDialogProps) {
  const [reminderType, setReminderType] = useState<'deadline' | 'update' | 'review' | 'custom'>('update');
  const [reminderDate, setReminderDate] = useState('');
  const [nextUpdateDate, setNextUpdateDate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [notifyAssignees, setNotifyAssignees] = useState(true);
  const [notifyMe, setNotifyMe] = useState(true);
  const [channels, setChannels] = useState<('app' | 'email' | 'push')[]>(['app']);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'daily' | 'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [showNextUpdatePrompt, setShowNextUpdatePrompt] = useState(true);

  // Get default dates
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const handleSave = () => {
    if (!reminderDate) {
      alert('Please select a reminder date');
      return;
    }

    const recipientIds: string[] = [];
    if (notifyMe) recipientIds.push(currentUserId);
    if (notifyAssignees) recipientIds.push(...assigneeIds);

    const reminder: Reminder = {
      id: generateId('reminder', entity.id),
      entityType,
      entityId: entity.id,
      entityTitle: 'title' in entity ? entity.title : 'name' in entity ? entity.name : '',
      reminderType,
      reminderDate,
      nextExpectedUpdateDate: nextUpdateDate || undefined,
      message: customMessage || undefined,
      recipientIds: [...new Set(recipientIds)], // Remove duplicates
      notificationChannels: channels,
      status: 'pending',
      createdBy: currentUserId,
      createdAt: new Date().toISOString(),
      recurring: isRecurring
        ? {
            frequency: recurringFrequency,
          }
        : undefined,
    };

    onSave(reminder, nextUpdateDate || undefined);
  };

  const handleQuickSetUpdate = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setNextUpdateDate(formatDate(date));
    setReminderDate(formatDate(date));
  };

  const toggleChannel = (channel: 'app' | 'email' | 'push') => {
    setChannels(prev =>
      prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Set Reminder & Update Schedule
            </CardTitle>
            <CardDescription className="mt-2">
              {'title' in entity ? entity.title : 'name' in entity ? entity.name : ''}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Next Update Date Prompt - Prominent */}
          {showNextUpdatePrompt && (
            <div className="bg-chart-5/10 border-2 border-chart-5/40 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-chart-5 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-chart-5 mb-2">
                    When do you expect to provide the next update?
                  </h4>
                  <p className="text-sm text-chart-5 mb-3">
                    Setting an expected update date helps keep stakeholders informed and track progress.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickSetUpdate(1)}
                      className="bg-white"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Tomorrow
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickSetUpdate(3)}
                      className="bg-white"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      3 Days
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickSetUpdate(7)}
                      className="bg-white"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      1 Week
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickSetUpdate(14)}
                      className="bg-white"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      2 Weeks
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Next Expected Update Date */}
          <div className="space-y-2">
            <Label htmlFor="nextUpdateDate" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Next Expected Update Date
            </Label>
            <Input
              id="nextUpdateDate"
              type="date"
              value={nextUpdateDate}
              onChange={e => setNextUpdateDate(e.target.value)}
              min={formatDate(today)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              When should you provide the next status update?
            </p>
          </div>

          {/* Reminder Type */}
          <div className="space-y-2">
            <Label>Reminder Type</Label>
            <Select value={reminderType} onValueChange={(v: any) => setReminderType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="update">Update Reminder</SelectItem>
                <SelectItem value="deadline">Deadline Reminder</SelectItem>
                <SelectItem value="review">Review Reminder</SelectItem>
                <SelectItem value="custom">Custom Reminder</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reminder Date */}
          <div className="space-y-2">
            <Label htmlFor="reminderDate">Reminder Date</Label>
            <Input
              id="reminderDate"
              type="date"
              value={reminderDate}
              onChange={e => setReminderDate(e.target.value)}
              min={formatDate(today)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">When to send the reminder notification</p>
          </div>

          {/* Custom Message */}
          {reminderType === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="customMessage">Custom Message</Label>
              <textarea
                id="customMessage"
                value={customMessage}
                onChange={e => setCustomMessage(e.target.value)}
                className="w-full min-h-[80px] px-3 py-2 border rounded-md"
                placeholder="Enter a custom reminder message..."
              />
            </div>
          )}

          {/* Recurring */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="recurring"
                checked={isRecurring}
                onCheckedChange={(checked) => setIsRecurring(checked as boolean)}
              />
              <Label htmlFor="recurring" className="flex items-center gap-2 cursor-pointer">
                <Repeat className="w-4 h-4" />
                Recurring Reminder
              </Label>
            </div>
            {isRecurring && (
              <Select value={recurringFrequency} onValueChange={(v: any) => setRecurringFrequency(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Recipients */}
          <div className="space-y-3">
            <Label>Notify</Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="notifyMe"
                  checked={notifyMe}
                  onCheckedChange={(checked) => setNotifyMe(checked as boolean)}
                />
                <Label htmlFor="notifyMe" className="cursor-pointer">Me</Label>
              </div>
              {assigneeIds.length > 0 && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="notifyAssignees"
                    checked={notifyAssignees}
                    onCheckedChange={(checked) => setNotifyAssignees(checked as boolean)}
                  />
                  <Label htmlFor="notifyAssignees" className="cursor-pointer">
                    All Assignees ({assigneeIds.length})
                  </Label>
                </div>
              )}
            </div>
          </div>

          {/* Notification Channels */}
          <div className="space-y-3">
            <Label>Notification Channels</Label>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="channel-app"
                  checked={channels.includes('app')}
                  onCheckedChange={() => toggleChannel('app')}
                />
                <Label htmlFor="channel-app" className="cursor-pointer">In-App</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="channel-email"
                  checked={channels.includes('email')}
                  onCheckedChange={() => toggleChannel('email')}
                />
                <Label htmlFor="channel-email" className="cursor-pointer">Email</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="channel-push"
                  checked={channels.includes('push')}
                  onCheckedChange={() => toggleChannel('push')}
                />
                <Label htmlFor="channel-push" className="cursor-pointer">Push</Label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <Bell className="w-4 h-4 mr-2" />
              Set Reminder
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
